import type { FeatureFlagDto } from '@portals/shared/portal/FeatureFlagDto';
import type { UserDetailsDto } from '@portals/shared/portal/UserDto';
import { ErrorContext } from '@portals/shared-frontend/hooks';
import { type JSX, useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary';
import FeatureFlagContext, { DEFAULT_FEATURE_FLAGS } from '@/hooks/FeatureFlagContext';

import { postUserLogin } from './api/user';
import OrganizationNotificationAlert from './components/OrganizationNotificationAlert';
import TermsOfUseAlert from './components/TermsOfUseAlert';
import { CELEBRUS_LOGIN_STATE_KEY, PORTAL_LOGIN_STATE_KEY } from './constants';
import useAuth from './hooks/useAuth';
import UserContext from './hooks/UserContext';
import { UserNavHistoryConfigProvider } from './provider/UserNavHistoryConfigProvider';
import { ApiError, request } from './request';

function pushUserLoginToCelebrus(userId?: string) {
  globalThis.dnbDataLayer?.push({
    event: 'GenericEvent',
    event_type: 'Status',
    event_action: 'User State Change',
    event_name: 'Logged in (authenticated)',
    event_value: globalThis.location.pathname,
  });

  globalThis.dnbDataLayer?.push({
    event: 'UserAuthenticated',
    user: {
      user_id: `dnb:devportal:${userId}}`,
      user_consent: '1',
      profile: {
        profile_id: `dnb:devportal:${userId}}`,
        role: 'personal',
      },
    },
  });
}

export default function App(): JSX.Element {
  const { isSignedIn, isLoading: authLoading } = useAuth();
  const { setGlobalError } = useContext(ErrorContext);
  const { pathname } = useLocation();
  const [user, setUser] = useState<UserDetailsDto>();
  const [userLoading, setUserLoading] = useState(true);

  const [featureFlags, setFeatureFlags] = useState<FeatureFlagDto>(DEFAULT_FEATURE_FLAGS);
  const [loadingFlags, setLoadingFlags] = useState<boolean>(true);

  const showOrganizationNotificationAlert =
    !!user && !!user.organizationName && !user.organizationAcknowledged && !pathname.includes('/help-center');

  const showTermsOfUsePopup = user?.latestTermsOfUseAccepted === false && pathname !== '/terms-of-use';
  const location = useLocation();

  useEffect(() => {
    globalThis.dnbDataLayer ??= [];
    globalThis?.dnbDataLayer?.push({
      event: 'PageLoaded',
      page: {},
    });
  }, [location]);

  const loadUser = useCallback(() => {
    if (isSignedIn) {
      setUserLoading(true);
      request<UserDetailsDto>('/user', 'get')
        .then(async (user) => {
          setUser(user);
          setUserLoading(false);

          const hasPushedLoginToCelebrus = sessionStorage.getItem(CELEBRUS_LOGIN_STATE_KEY);
          const hasPushedLoginToPortal = sessionStorage.getItem(PORTAL_LOGIN_STATE_KEY);
          if (!hasPushedLoginToCelebrus) {
            pushUserLoginToCelebrus(user.id);
            sessionStorage.setItem(CELEBRUS_LOGIN_STATE_KEY, 'true');
          }
          if (!hasPushedLoginToPortal) {
            await postUserLogin();
            sessionStorage.setItem(PORTAL_LOGIN_STATE_KEY, 'true');
          }
          return;
        })
        .catch(setGlobalError);
    } else {
      setUserLoading(false);
      setUser(undefined);
    }
  }, [isSignedIn, setGlobalError]);

  useEffect(() => {
    if (!authLoading) {
      loadUser();
    }
  }, [loadUser, authLoading]);

  const fetchFeatureFlags = useCallback(async () => {
    setLoadingFlags(true);
    try {
      const flags = await request<FeatureFlagDto>('/flags', 'get');
      setFeatureFlags(flags);
    } catch (error) {
      setGlobalError(ApiError.isApiError(error) ? error.status : 'unknown');
    }
    setLoadingFlags(false);
  }, [setGlobalError]);

  useEffect(() => {
    if (authLoading) return;
    void fetchFeatureFlags();
  }, [fetchFeatureFlags, isSignedIn, authLoading]);

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading: authLoading || userLoading,
        reload: loadUser,
      }}
    >
      <UserNavHistoryConfigProvider>
        <FeatureFlagContext.Provider
          value={{
            featureFlags: featureFlags ?? DEFAULT_FEATURE_FLAGS,
            isLoading: loadingFlags,
          }}
        >
          <ErrorBoundary>
            {showOrganizationNotificationAlert && <OrganizationNotificationAlert />}
            {showTermsOfUsePopup && <TermsOfUseAlert />}
            <Outlet />
          </ErrorBoundary>
        </FeatureFlagContext.Provider>
      </UserNavHistoryConfigProvider>
    </UserContext.Provider>
  );
}
